import React, { useState, useEffect } from 'react';
import { chatService } from '../../services/api';
import { TrashIcon } from '@heroicons/react/24/outline';

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [allConversations, setAllConversations] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({
    total_messages: 0,
    negative_feedback_count: 0,
    negative_feedback_percentage: 0,
    positive_feedback_count: 0,
    positive_feedback_percentage: 0
  });
  const [monthlyStats, setMonthlyStats] = useState({
    conversation_count: 0,
    estimated_cost: 0,
    month: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(true);
  const [isConversationsLoading, setIsConversationsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackError, setFeedbackError] = useState(null);
  const [conversationsError, setConversationsError] = useState(null);
  const [sections, setSections] = useState({
    kpis: true,
    leads: false,
    feedback: false
  });
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        setIsLoading(true);
        const response = await chatService.getLeads();
        console.log('Leads API response:', response);
        if (response && response.data) {
          setLeads(response.data);
        } else {
          console.error('Invalid response format:', response);
          setError('Invalid response format from server');
        }
      } catch (err) {
        console.error('Error loading leads:', err);
        setError(err.message || 'Error loading leads');
      } finally {
        setIsLoading(false);
      }
    };

    const fetchFeedback = async () => {
      try {
        setIsFeedbackLoading(true);
        const feedbackData = await chatService.getFeedbackWithContext();
        setFeedback(feedbackData);
        
        // Get feedback stats
        const stats = await chatService.getFeedbackStats();
        setFeedbackStats(stats);

        // Get monthly conversation stats
        const monthStats = await chatService.getMonthlyConversationStats();
        setMonthlyStats(monthStats);
      } catch (err) {
        console.error('Error loading feedback:', err);
        setFeedbackError(err.message || 'Error loading feedback');
      } finally {
        setIsFeedbackLoading(false);
      }
    };

    const fetchAllConversations = async () => {
      try {
        setIsConversationsLoading(true);
        setConversationsError(null);
        console.log('Starting to fetch all conversations...');
        const startTime = performance.now();
        
        const conversationsData = await chatService.getAllConversations();
        
        const endTime = performance.now();
        console.log(`Conversations loaded in ${Math.round(endTime - startTime)}ms`);
        console.log(`Loaded ${conversationsData.length} conversations`);
        
        setAllConversations(conversationsData);
      } catch (err) {
        console.error('Error loading all conversations:', err);
        setConversationsError(err.message || 'Error loading conversations');
      } finally {
        setIsConversationsLoading(false);
      }
    };

    fetchLeads();
    fetchFeedback();
    fetchAllConversations();
  }, []);

  const toggleSection = (section) => {
    setSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm('Sind Sie sicher, dass Sie diesen Interessenten löschen möchten?')) {
      try {
        await chatService.deleteLead(leadId);
        // Update leads list after deletion
        const response = await chatService.getLeads();
        if (response && response.data) {
          setLeads(response.data);
        }
      } catch (err) {
        console.error('Error deleting lead:', err);
        setError(err.message || 'Error deleting lead');
      }
    }
  };

  const handleShowConversation = (conversation) => {
    setSelectedConversation(conversation);
    setShowConversationModal(true);
  };

  const closeConversationModal = () => {
    setShowConversationModal(false);
    setSelectedConversation(null);
  };

  const SectionHeader = ({ title, section }) => (
    <div 
      className="flex items-center mb-6 cursor-pointer hover:opacity-80"
      onClick={() => toggleSection(section)}
    >
      <h1 className="text-3xl font-bold">{title}</h1>
      <div className="ml-3 text-gray-400">
        {sections[section] ? (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
      </div>
    </div>
  );

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500',
      contacted: 'bg-yellow-500',
      qualified: 'bg-green-500',
      unqualified: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return dateString;
    }
  };

  const translateStatus = (status) => {
    const translations = {
      'new': 'Neu',
      'contacted': 'Kontaktiert',
      'qualified': 'Qualifiziert',
      'unqualified': 'Nicht Qualifiziert'
    };
    return translations[status] || status;
  };

  const translateFeedbackType = (type) => {
    const translations = {
      'incorrect_information': 'Falsche Information',
      'not_helpful': 'Nicht Hilfreich',
      'unclear': 'Unklar',
      'other': 'Sonstiges'
    };
    return translations[type] || type || 'Kein Typ';
  };

  const isValidLead = (lead) => {
    // Check for required combinations:
    // 1. Company name + Contact name
    // 2. Company name + Email
    // 3. Contact name + Email
    return (
      (lead.company_name && lead.contact_name) ||
      (lead.company_name && lead.email) ||
      (lead.contact_name && lead.email)
    );
  };

  const ConversationModal = ({ isOpen, onClose, conversation }) => {
    if (!isOpen || !conversation) return null;

    // Create a map of message IDs to their feedback details
    const feedbackMap = {};
    if (conversation.feedback_details) {
      conversation.feedback_details.forEach(feedback => {
        feedbackMap[feedback.message_id] = feedback;
      });
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold">Gespräch anzeigen</h2>
              <div className="flex items-center mt-2 space-x-3">
                <span className="text-sm text-gray-400">Benutzer:</span>
                {conversation.user_type === 'registered' ? (
                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-1 bg-green-600 rounded-full text-xs">
                      Registriert
                    </span>
                    <span className="text-sm text-gray-300">
                      {conversation.user_email}
                    </span>
                  </div>
                ) : (
                  <span className="px-2 py-1 bg-gray-600 rounded-full text-xs">
                    Anonym
                  </span>
                )}
                <span className="text-sm text-gray-400">•</span>
                <span className="text-sm text-gray-400">
                  {conversation.message_count} Nachrichten
                </span>
                <span className="text-sm text-gray-400">•</span>
                <span className="text-sm text-gray-400">
                  {formatDate(conversation.created_at)}
                </span>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="space-y-4">
            {conversation.full_conversation && conversation.full_conversation.map((message, index) => {
              const messageFeedback = feedbackMap[message.id];
              
              return (
                <div 
                  key={index} 
                  className={`p-4 rounded-lg ${message.role === 'user' ? 'bg-gray-700' : 'bg-gray-600'}`}
                >
                  <div className="flex justify-between mb-2">
                    <span className="font-semibold">
                      {message.role === 'user' ? 'Benutzer' : 'Assistent'}
                    </span>
                    <span className="text-sm text-gray-400">
                      {formatDate(message.created_at)}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap mb-2">
                    {typeof message.content === 'string' ? message.content : JSON.stringify(message.content, null, 2)}
                  </p>
                  
                  {/* Show feedback if this message has feedback */}
                  {messageFeedback && (
                    <div className="mt-3 p-3 bg-gray-900 rounded border-l-4 border-red-500">
                      <div className="flex items-center mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          messageFeedback.is_positive ? 'bg-green-600' : 'bg-red-600'
                        }`}>
                          {messageFeedback.is_positive ? 'Positives Feedback' : 'Negatives Feedback'}
                        </span>
                        {!messageFeedback.is_positive && messageFeedback.feedback_type && (
                          <span className="ml-2 text-xs text-gray-400">
                            ({translateFeedbackType(messageFeedback.feedback_type)})
                          </span>
                        )}
                        <span className="ml-auto text-xs text-gray-400">
                          {formatDate(messageFeedback.created_at)}
                        </span>
                      </div>
                      {messageFeedback.feedback_text && (
                        <p className="text-sm text-gray-300">
                          <strong>Kommentar:</strong> {messageFeedback.feedback_text}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-gray-900 text-white overflow-y-auto">
      <div className="p-8">
        {/* KPIs Section */}
        <div className="mb-12">
          <SectionHeader title="Kennzahlen" section="kpis" />
          {sections.kpis && (
            <div className="grid grid-cols-3 gap-6">
              {/* Monthly Activity */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Aktivität im {monthlyStats.month}</h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-400 mb-1">Gesamte Gespräche</p>
                    <p className="text-3xl font-bold">{monthlyStats.conversation_count}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">Geschätzte Kosten</p>
                    <p className="text-3xl font-bold">€{monthlyStats.estimated_cost.toFixed(2).replace('.', ',')}</p>
                  </div>
                </div>
              </div>

              {/* Conversation Ratings */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Gesprächsbewertungen</h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-400 mb-1">Negative Bewertungen</p>
                    <p className="text-3xl font-bold text-red-500">{feedbackStats.negative_feedback_percentage}%</p>
                    <p className="text-sm text-gray-400">({feedbackStats.negative_feedback_count} von {feedbackStats.total_messages} Nachrichten)</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">Positive Bewertungen</p>
                    <p className="text-3xl font-bold text-green-500">{feedbackStats.positive_feedback_percentage}%</p>
                    <p className="text-sm text-gray-400">({feedbackStats.positive_feedback_count} von {feedbackStats.total_messages} Nachrichten)</p>
                  </div>
                </div>
              </div>

              {/* Empty Column */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Zukünftige Kennzahlen</h2>
                <p className="text-gray-400">Weitere Metriken folgen in Kürze</p>
              </div>
            </div>
          )}
        </div>

        {/* Leads Section */}
        <div className="mb-12">
          <SectionHeader title="Interessenten" section="leads" />
          {sections.leads && (
            isLoading ? (
              <div className="text-center py-8">
                <p className="text-gray-400">Lade Interessenten...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-400">{error}</p>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Unternehmen</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Kontakt</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Standort</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Projektbeschreibung</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">E-Mail</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Status</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Erstellt</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {leads.filter(isValidLead).length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-8 text-center text-gray-400">
                            Noch keine qualifizierten Leads
                          </td>
                        </tr>
                      ) : (
                        leads.filter(isValidLead).map((lead) => (
                          <tr key={lead.id} className="hover:bg-gray-700">
                            <td className="px-6 py-4 text-sm">{lead.company_name || '-'}</td>
                            <td className="px-6 py-4 text-sm">{lead.contact_name || '-'}</td>
                            <td className="px-6 py-4 text-sm">{lead.location || '-'}</td>
                            <td className="px-6 py-4 text-sm max-w-md">
                              <p 
                                className="text-sm text-white line-clamp-2 hover:line-clamp-none cursor-pointer"
                                title={lead.project_description || '-'}
                              >
                                {lead.project_description || '-'}
                              </p>
                            </td>
                            <td className="px-6 py-4 text-sm">{lead.email || '-'}</td>
                            <td className="px-6 py-4 text-sm">
                              <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(lead.status)}`}>
                                {translateStatus(lead.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm">{formatDate(lead.created_at)}</td>
                            <td className="px-6 py-4 text-sm">
                              <button
                                onClick={() => handleDeleteLead(lead.id)}
                                className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-red-500 transition-colors"
                                title="Löschen"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}
        </div>

        {/* Conversation Feedback Section */}
        <div>
          <SectionHeader title="Gesprächs-Feedback" section="feedback" />
          {sections.feedback && (
            <div className="bg-gray-800 rounded-lg overflow-hidden">
              {isConversationsLoading ? (
                <div className="text-center py-8">
                  <p className="text-gray-400">Lade Gespräche...</p>
                </div>
              ) : conversationsError ? (
                <div className="text-center py-8">
                  <p className="text-red-400">{conversationsError}</p>
                </div>
              ) : allConversations.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400">Keine Gespräche vorhanden</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Erstellt</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Benutzer</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Erste Nachricht</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Nachrichten</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Feedback Status</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Feedback Details</th>
                        <th className="px-6 py-3 text-left text-sm font-semibold">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {allConversations.map((conversation) => (
                        <tr key={conversation.conversation_id} className="hover:bg-gray-700">
                          <td className="px-6 py-4 text-sm">
                            {formatDate(conversation.created_at)}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {conversation.user_type === 'registered' ? (
                              <div>
                                <span className="px-2 py-1 bg-green-600 rounded-full text-xs">
                                  Registriert
                                </span>
                                <p className="text-xs text-gray-400 mt-1">
                                  {conversation.user_email}
                                </p>
                              </div>
                            ) : (
                              <span className="px-2 py-1 bg-gray-600 rounded-full text-xs">
                                Anonym
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm max-w-md">
                            <p 
                              className="text-sm text-white line-clamp-2 hover:line-clamp-none cursor-pointer"
                              title={conversation.first_user_message}
                            >
                              {conversation.first_user_message}
                            </p>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {conversation.message_count}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {conversation.has_feedback ? (
                              <span className="px-2 py-1 bg-red-500 rounded-full text-xs">
                                Hat Feedback
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-500 rounded-full text-xs">
                                Kein Feedback
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {conversation.has_feedback && conversation.feedback_details.length > 0 ? (
                              <div className="space-y-1">
                                {conversation.feedback_details.map((feedback, index) => (
                                  <div key={index} className="text-xs">
                                    <span className={`px-1 py-0.5 rounded text-xs ${
                                      feedback.is_positive ? 'bg-green-600' : 'bg-red-600'
                                    }`}>
                                      {feedback.is_positive ? 'Positiv' : translateFeedbackType(feedback.feedback_type)}
                                    </span>
                                    {!feedback.is_positive && feedback.feedback_text && (
                                      <p className="text-gray-400 mt-1 line-clamp-1" title={feedback.feedback_text}>
                                        {feedback.feedback_text}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <button
                              onClick={() => handleShowConversation(conversation)}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs transition-colors"
                            >
                              Gespräch anzeigen
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Conversation Modal */}
      <ConversationModal 
        isOpen={showConversationModal}
        onClose={closeConversationModal}
        conversation={selectedConversation}
      />
    </div>
  );
};

export default AdminDashboard;
